import { useState } from "react";
import { MenuItem, TextField } from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { pt } from "date-fns/locale";
import { format, isValid, parseISO, subDays } from "date-fns";
import { DATE_FILTER_LABELS, DEFAULT_DATE_FILTER_PRESETS, resolveDateFilterRange, resolveDatePreset } from "./datePresets";
import { DateFilterKey, GlobalFilterValues } from "./types";

const SEED_FALLBACK_PRESET = "ultimos_30_dias";

const parseDayValue = (dayValue: string | number | null | undefined) => {
    if (typeof dayValue !== "string" || dayValue === "") return null;
    const parsed = parseISO(dayValue);
    return isValid(parsed) ? parsed : null;
};

type DashboardDateFilterProps = {
    label: string;
    filterKey: string;
    values: GlobalFilterValues;
    onChange: (filterKey: string, value: string | number | null) => void;
    presets?: Array<DateFilterKey>;
};

// Filtro de datas partilhado por todos os dashboards nativos: dropdown de presets
// (subset por dashboard) + opção "Personalizado" SEMPRE presente, que revela dois
// DatePickers De/Até ("Até" inclusivo na UI; o range guardado converte para lt exclusivo).
// Os pickers renderizam a partir de rascunho local (permite estados intermédios inválidos
// enquanto se edita) e só propagam para os values `${key}_from`/`${key}_to` pares válidos —
// os widgets mantêm sempre o último range válido.
export const DashboardDateFilter = ({ label, filterKey, values, onChange, presets }: DashboardDateFilterProps) => {
    const [draftFromDate, setDraftFromDate] = useState<Date | null>(() => parseDayValue(values[`${filterKey}_from`]));
    const [draftToDate, setDraftToDate] = useState<Date | null>(() => parseDayValue(values[`${filterKey}_to`]));

    const presetOptions: Array<DateFilterKey> = [...(presets ?? DEFAULT_DATE_FILTER_PRESETS), "personalizado"];
    const selectedValue = (values[filterKey] as string) || presetOptions[0];

    const commitRangeIfValid = (fromDate: Date | null, toDate: Date | null) => {
        if (!fromDate || !toDate || !isValid(fromDate) || !isValid(toDate) || fromDate > toDate) return;
        onChange(`${filterKey}_from`, format(fromDate, "yyyy-MM-dd"));
        onChange(`${filterKey}_to`, format(toDate, "yyyy-MM-dd"));
    };

    const switchToCustomRange = () => {
        const seedRange = resolveDateFilterRange(values, filterKey, new Date()) ?? resolveDatePreset(SEED_FALLBACK_PRESET, new Date());
        const seedFromDate = parseISO(seedRange.gte);
        const seedToDate = subDays(parseISO(seedRange.lt), 1);
        setDraftFromDate(seedFromDate);
        setDraftToDate(seedToDate);
        onChange(filterKey, "personalizado");
        commitRangeIfValid(seedFromDate, seedToDate);
    };

    const handlePresetChange = (newValue: string) => {
        if (newValue === "personalizado") {
            switchToCustomRange();
            return;
        }
        onChange(filterKey, newValue);
    };

    const handleFromChange = (newFromDate: Date | null) => {
        setDraftFromDate(newFromDate);
        commitRangeIfValid(newFromDate, draftToDate);
    };

    const handleToChange = (newToDate: Date | null) => {
        setDraftToDate(newToDate);
        commitRangeIfValid(draftFromDate, newToDate);
    };

    return (
        <>
            <TextField
                select
                size="small"
                label={label}
                value={selectedValue}
                onChange={(event) => handlePresetChange(event.target.value)}
                sx={{ minWidth: 180 }}
            >
                {presetOptions.map((presetKey) => (
                    <MenuItem key={presetKey} value={presetKey}>
                        {DATE_FILTER_LABELS[presetKey]}
                    </MenuItem>
                ))}
            </TextField>
            {selectedValue === "personalizado" && (
                <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={pt}>
                    <DatePicker
                        label="De"
                        value={draftFromDate}
                        onChange={handleFromChange}
                        format="dd/MM/yyyy"
                        slotProps={{ textField: { size: "small" } }}
                        sx={{ width: 180 }}
                    />
                    <DatePicker
                        label="Até"
                        value={draftToDate}
                        onChange={handleToChange}
                        format="dd/MM/yyyy"
                        slotProps={{ textField: { size: "small" } }}
                        sx={{ width: 180 }}
                    />
                </LocalizationProvider>
            )}
        </>
    );
};
